export const QUOTE_UPLOAD_BUCKET = 'quote-uploads';
export const QUOTE_UPLOAD_MAX_BYTES = 20 * 1024 * 1024;
export const QUOTE_UPLOAD_ACCEPT = '.pdf,.ai,.eps,.psd,.jpg,.jpeg,.png,.svg,.zip';

const ALLOWED_EXTENSIONS = new Set(QUOTE_UPLOAD_ACCEPT.split(','));
const filesByQuoteItemId = new Map();

function fileExtension(name) {
  const match = String(name || '').toLowerCase().match(/\.[^.]+$/);
  return match?.[0] || '';
}

function safeFileName(name) {
  const cleaned = String(name || 'design-file')
    .replace(/[\\/]+/g, '-')
    .replace(/[^a-zA-Z0-9._ -]+/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^\.+/, '')
    .slice(-140);

  return cleaned || 'design-file';
}

function uploadFolderId() {
  return window.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function validateQuoteUploadFile(file) {
  if (!file || !ALLOWED_EXTENSIONS.has(fileExtension(file.name))) {
    return 'Only PDF, AI, EPS, PSD, JPG, JPEG, PNG, SVG, and ZIP files are allowed.';
  }

  if (file.size > QUOTE_UPLOAD_MAX_BYTES) {
    return `${file.name} is larger than the 20 MB limit.`;
  }

  return '';
}

export function setQuoteUploadFiles(itemId, files) {
  const selected = Array.from(files || []);
  if (selected.length) {
    filesByQuoteItemId.set(itemId, selected);
  } else {
    filesByQuoteItemId.delete(itemId);
  }
}

export function getQuoteUploadFiles(itemId) {
  return filesByQuoteItemId.get(itemId) || [];
}

export function clearQuoteUploadFiles(itemId) {
  filesByQuoteItemId.delete(itemId);
}

export function moveQuoteUploadFiles(fromItemId, toItemId, { keepExisting = false } = {}) {
  const existing = getQuoteUploadFiles(toItemId);
  const source = getQuoteUploadFiles(fromItemId);

  if (!keepExisting || !existing.length) setQuoteUploadFiles(toItemId, source);
  clearQuoteUploadFiles(fromItemId);
}

export function collectQuoteUploadFiles(configuration) {
  return Array.from(configuration?.querySelectorAll('input[type="file"]') || [])
    .flatMap((input) => Array.from(input.files || []));
}

export function quoteUploadValidationError(items) {
  for (const item of items) {
    const files = getQuoteUploadFiles(item.id);
    const savedNames = item.specifications?.['Upload Design Files'];

    if (savedNames && !files.length) {
      return 'Please reselect your design files before submitting this quote.';
    }

    for (const file of files) {
      const error = validateQuoteUploadFile(file);
      if (error) return error;
    }
  }

  return '';
}

export async function removeQuoteUploads(storage, paths) {
  if (!paths.length) return;
  await storage.from(QUOTE_UPLOAD_BUCKET).remove(paths);
}

export async function uploadQuoteDesignFiles(items, storage, onProgress = () => {}) {
  const total = items.reduce((sum, item) => sum + getQuoteUploadFiles(item.id).length, 0);
  if (!total) return { items, paths: [] };

  const bucket = storage.from(QUOTE_UPLOAD_BUCKET);
  const paths = [];
  let completed = 0;

  try {
    const uploadedItems = [];

    for (const item of items) {
      const files = getQuoteUploadFiles(item.id);
      if (!files.length) {
        uploadedItems.push(item);
        continue;
      }

      const folder = uploadFolderId();
      const usedNames = new Set();
      const uploadedFiles = [];

      for (const file of files) {
        const validationError = validateQuoteUploadFile(file);
        if (validationError) throw new Error(validationError);

        const baseName = safeFileName(file.name);
        let storedName = baseName;
        let duplicate = 2;
        while (usedNames.has(storedName)) {
          storedName = `${duplicate}-${baseName}`;
          duplicate += 1;
        }
        usedNames.add(storedName);

        const path = `quotes/${folder}/${storedName}`;
        onProgress({ completed, currentFile: file.name, total });

        const { error } = await bucket.upload(path, file, {
          cacheControl: '3600',
          contentType: file.type || undefined,
          upsert: false,
        });
        if (error) throw error;

        paths.push(path);
        const { data: publicUrlData } = bucket.getPublicUrl(path);
        uploadedFiles.push({
          name: file.name,
          path,
          url: publicUrlData?.publicUrl || '',
          type: file.type || '',
          size: file.size,
        });

        completed += 1;
        onProgress({ completed, currentFile: file.name, total });
      }

      uploadedItems.push({
        ...item,
        specifications: {
          ...(item.specifications || {}),
          upload_design_files: uploadedFiles,
        },
      });
    }

    return { items: uploadedItems, paths };
  } catch (error) {
    await removeQuoteUploads(storage, paths).catch(() => {});
    const uploadError = new Error('The design files could not be uploaded. Please try again.');
    uploadError.code = 'quote_upload_failed';
    uploadError.cause = error;
    throw uploadError;
  }
}
